/**
 * Which uploaded documents may be deleted from storage.
 *
 * Kept free of Vue/Vuex and of Firebase so it can be unit tested with plain
 * node (tests/unit/documentImportUploadCleanup.test.js). It decides; the store
 * does the deleting.
 *
 * The browser uploads a document to Firebase before the parse and sends the
 * server its URL, because sending the bytes meant 7-10MB per import travelling
 * to the worker inside a Redis job argument, which filled a 25MB instance and
 * wedged the queue. The upload moving earlier is what makes cleanup necessary:
 * a failed import now leaves a file behind where before nothing was uploaded.
 *
 * The asymmetry here is deliberate. A voucher IS the traveller's document --
 * saved on the trip, opened from the ride page, sent over WhatsApp -- so
 * deleting one destroys something that cannot be recovered, while leaving a
 * stray file costs a few KB. So: answer "keep" unless nothing can reference it.
 */

/**
 * Types whose document is kept and attached to the trip as its voucher.
 * Mirrors buildAttachOperations, which saves `voucherUrls` for an
 * accommodation and for every other type except an itinerary.
 */
const VOUCHER_TYPES = ['accommodation', 'flight', 'car_rental', 'attraction', 'show'];

/**
 * Types uploaded only so the model can read them. An itinerary's URL is never
 * persisted -- buildAttachOperations excludes it explicitly -- so once the
 * extraction has succeeded the file has no further use.
 */
const EXTRACTION_ONLY_TYPES = ['itinerary'];

/** Outcomes that mean nothing will ever reference the file. */
const DEAD_STATUSES = ['error', 'not_accommodation'];

/** True only when this result's file is certainly unreferenced. */
function shouldDeleteUpload(result) {
  if (!result || !result.uploadPath) return false;

  // Still in flight: its type is not known yet, so nothing can be concluded.
  if (result.status === 'processing') return false;

  // Discarding is one-way in this store, and a discarded card is left out of
  // buildAttachOperations, so its document is never saved.
  if (result.discarded) return true;

  if (DEAD_STATUSES.indexOf(result.status) !== -1) return true;

  if (result.status === 'done') return EXTRACTION_ONLY_TYPES.indexOf(result.type) !== -1;

  // An unknown status, like an unknown type, is a reason to keep the file.
  return false;
}

/**
 * Every file safe to delete across the whole list, as {index, path}.
 *
 * Two cards can come from one upload -- an itinerary and an accommodation found
 * inside it, say -- so a path is only swept when EVERY card holding it agrees,
 * which is the guard that stops an itinerary's cleanup taking a voucher with
 * it. Each path is returned once.
 */
function uploadsToDelete(results) {
  const list = Array.isArray(results) ? results : [];

  const keep = {};
  list.forEach((result) => {
    if (result && result.uploadPath && !shouldDeleteUpload(result)) {
      keep[result.uploadPath] = true;
    }
  });

  const seen = {};
  const out = [];
  list.forEach((result, index) => {
    if (!shouldDeleteUpload(result)) return;
    const path = result.uploadPath;
    if (keep[path] || seen[path]) return;
    seen[path] = true;
    out.push({ index, path });
  });
  return out;
}

module.exports = {
  VOUCHER_TYPES,
  EXTRACTION_ONLY_TYPES,
  shouldDeleteUpload,
  uploadsToDelete,
};
